import { Box, Button, Flex, Text } from '@everlywell/ui-kit';
import Price from 'components/Price';
import { PurchaseOptionsDrawerProduct } from 'components/PurchaseOptionsDrawerProvider/PurchaseOptionsDrawerProvider.context';
import React from 'react';
import { discountedPrice, Promo } from 'utils/helpers/activePromoHelpers';
import { SubscriptionVariantType } from 'utils/types';

import { PurchaseOptionType, MembershipOptionType } from '../PurchaseOptionsDrawer';

export type PreCartFooterProps = {
  selectedOption: PurchaseOptionType;
  variant?: SubscriptionVariantType;
  price: string;
  onClick: (ctaText: string) => void;
  isEverlywellPlus?: boolean;
  isLoading: boolean;
  selectedSubscriptionOption?: MembershipOptionType | null;
  product?: PurchaseOptionsDrawerProduct | null | undefined;
  activePromo: Promo | null;
};

/**
 * PreCart Footer.
 */
function PreCartFooter(props: PreCartFooterProps): JSX.Element {
  const {
    selectedOption,
    variant,
    price,
    onClick,
    isEverlywellPlus,
    isLoading,
    selectedSubscriptionOption,
    product,
    activePromo,
  } = props;

  const isSubscription = selectedOption === PurchaseOptionType.SubscribeAndSave;
  const oneTimePurchase = selectedOption === PurchaseOptionType.OneTime;

  const subscriptionFrequency = variant?.subscription_plan?.name.toLowerCase();

  const productPriceNumber = parseFloat(price ?? '0');

  const hasEverlywellCheckout = selectedSubscriptionOption && isEverlywellPlus;

  const discountedPromoPrice = parseFloat(
    discountedPrice(price ?? '0', activePromo ? activePromo : undefined),
  );

  const showPromo =
    Boolean(activePromo && product?.promotionable) &&
    productPriceNumber > discountedPromoPrice;



  const getButtonData = () => {
    if (!isEverlywellPlus) {
      return {
        label: 'Add to Cart',
        to: '',
        testId: 'pre-cart-cta-add-to-cart',
      };
    }

    if (hasEverlywellCheckout) {
      return {
        label: 'Join Now',
        to: `/ewplus-checkout/?slug=${selectedSubscriptionOption}`,
        testId: 'pre-cart-cta-join-now',
      };
    }

    return {
      label: 'Join today',
      to: '/360-full-body-test/',
      testId: 'pre-cart-cta-view-membership-options',
    };
  };

  const buttonData = getButtonData();

  return (
    <Box
      backgroundColor={'tints.lightCream'}
      borderRadius={'24px 24px 0 0'}
      paddingTop={'20px'}
      paddingBottom={'34px'}
      paddingX={6}
      boxShadow="0px 4px 35px 0px rgba(0, 0, 0, 0.2)"
    >
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'space-between'}
      >
        <Text
          display={'flex'}
          flexDirection={'column'}
          fontWeight={'medium'}
          fontSize={'lg'}
        >
          Subtotal
          <Text color="tints.darkGrey" fontSize={'md'}>
            {isSubscription
              ? `Billed ${subscriptionFrequency}`
              : 'One time purchase'}
          </Text>
        </Text>
        {showPromo && oneTimePurchase ? (
          <Flex flexDirection="column">
            <Flex color="tints.uiGrey" justifyContent="flex-end">
              <Price price={productPriceNumber} strikeThrough />
            </Flex>
            <Flex
              color="utility.sale"
              flexDirection="row"
              justifyContent={'flex-end'}
              alignItems="baseline"
              gap={1}
              flexWrap={'wrap'}
            >
              <Flex flexDirection={'row'} alignItems={'baseline'} gap={1}>
                <Price price={discountedPromoPrice} />
                <Text>with code</Text>
              </Flex>
              <Text wordBreak="break-word" overflowWrap="break-word">
                {activePromo?.code?.toUpperCase()}
              </Text>
            </Flex>
          </Flex>
        ) : (
          <Price price={productPriceNumber} />
        )}
      </Box>

      <Button
        data-testid={buttonData.testId}
        variant="primary"
        colorScheme="greenCS"
        size="wide"
        marginTop={3}
        onClick={() => onClick(buttonData.label)}
        isLoading={isLoading}
      >
        {buttonData.label}
      </Button>
    </Box>
  );
}

export default PreCartFooter;
